/**
 * Portal feature flag module.
 *
 * Resolution order (first defined value wins):
 *   1. `import.meta.env[envKey]` — build-time / deploy-time toggle.
 *   2. `window.localStorage[storageKey]` — runtime QA override.
 *   3. `default` — fallback when neither source provides a value.
 *
 * Env vars must use the `NA_FEATURE_*` prefix (allowlisted in
 * `apps/portal/vite.config.ts`). LocalStorage keys use `na:feature:<name>`.
 */

interface FeatureDefinition {
  /** import.meta.env key (e.g. "NA_FEATURE_AUDIT_LOGS"). */
  envKey: string;
  /** localStorage key for runtime override. */
  storageKey: string;
  /** Default when neither env nor localStorage provides a value. */
  default: boolean;
}

const DEFS = {
  auditLogs: {
    envKey: "NA_FEATURE_AUDIT_LOGS",
    storageKey: "na:feature:auditLogs",
    default: false,
  },
  toolDistribution: {
    envKey: "NA_FEATURE_TOOL_DISTRIBUTION",
    storageKey: "na:feature:toolDistribution",
    // On for everyone; NA_FEATURE_TOOL_DISTRIBUTION=false at build (or localStorage) is
    // the kill switch. Cells without a catalog show a "not configured" state.
    default: true,
  },
} satisfies Record<string, FeatureDefinition>;

export type FeatureName = keyof typeof DEFS;

function readBool(value: unknown): boolean | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const v = String(value).toLowerCase();
  if (v === "1" || v === "true" || v === "on") return true;
  if (v === "0" || v === "false" || v === "off") return false;
  return undefined;
}

function evaluate(def: FeatureDefinition): boolean {
  const env = readBool(
    (import.meta.env as Record<string, unknown>)[def.envKey],
  );
  if (env !== undefined) return env;

  try {
    const ls = readBool(window.localStorage.getItem(def.storageKey));
    if (ls !== undefined) return ls;
  } catch {
    /* SSR / disabled storage */
  }

  return def.default;
}

export const features = Object.fromEntries(
  Object.entries(DEFS).map(([k, def]) => [k, evaluate(def)]),
) as Record<FeatureName, boolean>;

export function useFeature(name: FeatureName): boolean {
  return features[name];
}

/* ------------------------------------------------------------------ */
/* Availability registry (see handoff 07-coming-soon-and-api-matrix)   */
/* ------------------------------------------------------------------ */

export type Availability = "available" | "comingSoon" | "hidden";

/**
 * What ships today for each area that depends on a missing ctrl API.
 * Flip an entry to "available" when its endpoint lands.
 */
const AVAILABILITY = {
  /** GitLab remote development workspaces: needs the ctrl GraphQL proxy. */
  workspaces: () => "comingSoon",
  /** Platform › Cluster (`/platform/cluster`). Cordon / drain still ship as Soon. */
  clusterMetrics: () => "available",
  /** Scenario step list and run history. */
  scenarioHistory: () => "comingSoon",
  /** Tenant suspend / resume: no endpoint. */
  tenantSuspend: () => "comingSoon",
  /** Change a member's tenant role: no `PUT /tenants/{id}/members/{userId}`. */
  memberRole: () => "comingSoon",
  /** Signed-in CLI / extension clients and revoke. */
  cliClients: () => "comingSoon",
  /** Location map tiles: no tile provider decided. */
  locationMap: () => "comingSoon",
  /** VCS provider "Test connection": no endpoint. */
  vcsTest: () => "comingSoon",
  auditLogs: () => (evaluate(DEFS.auditLogs) ? "comingSoon" : "hidden"),
} satisfies Record<string, () => Availability>;

export type AvailabilityKey = keyof typeof AVAILABILITY;

const OVERRIDE_KEY = "na:features";

/**
 * Dev-only override: `?features=workspaces:available,clusterMetrics:available`.
 * Persisted for the browser session so navigation keeps it.
 */
function overrides(): Partial<Record<AvailabilityKey, Availability>> {
  if (!import.meta.env.DEV) return {};
  try {
    const q = new URLSearchParams(window.location.search).get("features");
    if (q !== null && q !== window.sessionStorage.getItem(OVERRIDE_KEY)) window.sessionStorage.setItem(OVERRIDE_KEY, q);
    const raw = q ?? window.sessionStorage.getItem(OVERRIDE_KEY) ?? "";
    return Object.fromEntries(
      raw
        .split(",")
        .map((p) => p.split(":"))
        .filter(([k, v]) => k && v),
    ) as Partial<Record<AvailabilityKey, Availability>>;
  } catch {
    return {};
  }
}

export function availability(key: AvailabilityKey): Availability {
  return overrides()[key] ?? AVAILABILITY[key]();
}
